import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Loader2, FileWarning } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InsuranceVerify({ booking, token, onSaved }) {
  const [verifying, setVerifying] = useState(false);
  const v = booking.insurance_verification;

  const verify = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/bookings/${booking.id}/verify-insurance`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Insurance card analyzed.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify insurance.");
    } finally {
      setVerifying(false);
    }
  };

  const badge = !v
    ? { icon: ShieldQuestion, cls: "text-white/50 border-white/15 bg-white/5", label: "Insurance not checked" }
    : v.status === "valid"
    ? { icon: ShieldCheck, cls: "text-green-400 border-green-500/40 bg-green-500/10", label: "Insurance valid" }
    : v.status === "expired"
    ? { icon: ShieldAlert, cls: "text-red-400 border-red-500/40 bg-red-500/10", label: "Insurance EXPIRED" }
    : v.status === "name_mismatch"
    ? { icon: ShieldAlert, cls: "text-red-400 border-red-500/40 bg-red-500/10", label: "Named insured mismatch" }
    : v.status === "review"
    ? { icon: FileWarning, cls: "text-amber-400 border-amber-400/40 bg-amber-400/10", label: "Needs review" }
    : { icon: FileWarning, cls: "text-amber-400 border-amber-400/40 bg-amber-400/10", label: "Card unreadable" };
  const Icon = badge.icon;

  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-[#0A0A0A] p-3" data-testid={`insurance-verify-${booking.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={`text-xs px-2.5 py-1 rounded-full border flex items-center gap-1.5 ${badge.cls}`} data-testid={`insurance-badge-${booking.id}`}>
          <Icon size={13} /> {badge.label}
        </span>
        <button data-testid={`verify-insurance-btn-${booking.id}`} onClick={verify} disabled={verifying || !booking.insurance_card_path}
          className="text-xs flex items-center gap-1.5 border border-[#F2B705]/50 text-[#F2B705] px-3 py-1.5 rounded hover:bg-[#F2B705] hover:text-black transition-colors disabled:opacity-40"
          title={!booking.insurance_card_path ? "No insurance card image on file" : ""}>
          {verifying ? <Loader2 className="animate-spin" size={13} /> : <ShieldCheck size={13} />} {v ? "Re-check" : "AI Check Insurance"}
        </button>
      </div>

      {v && v.status !== "unreadable" && (
        <div className="mt-2 text-xs text-white/55 space-y-0.5">
          {v.insurer ? <div>Insurer: <span className="text-white/75">{v.insurer}</span></div> : null}
          {v.policy_number ? <div>Policy #: <span className="text-white/75">{v.policy_number}</span></div> : null}
          {v.named_insured ? <div>Named insured: <span className="text-white/75">{v.named_insured}</span> {v.name_match
            ? <span className="text-green-400">✓ matches renter</span>
            : <span className="text-red-400">✗ not the renter</span>}</div> : null}
          {v.expiration_date ? <div>Expires: <span className={v.expired ? "text-red-400" : "text-white/75"}>{v.expiration_date}</span> {v.expired && <span className="text-red-400">— expired before pickup</span>}</div> : null}
          {v.vehicles ? <div>Covered vehicles: <span className="text-white/75">{v.vehicles}</span></div> : null}
        </div>
      )}
      {v && v.status === "unreadable" && (
        <p className="mt-2 text-xs text-amber-400/80">Couldn't read the uploaded image as an insurance card. Ask the renter to upload a clear photo of the front of their insurance ID card.</p>
      )}
    </div>
  );
}
